<template>
  <div class="detail-page">
    <!-- 返回头部 -->
    <div class="back-header" @click="$emit('backList')">
      <img
        src="@/assets/icons/i-return.png"
        style="width: 36px; height: 36px; cursor: pointer"
      />
      <span>{{ folderInfo.name }}</span>
    </div>

    <!-- 顶部分类标签 -->
    <div class="tab-group">
      <div class="tab-wrap">
        <div
          v-for="tabItem in tabList"
          :key="tabItem.value"
          class="tab-item"
          :class="{ active: activeTab === tabItem.value }"
          @click="
            activeTab = tabItem.value;
            filterData();
          "
        >
          {{ tabItem.label }}
        </div>
      </div>
    </div>

    <!-- 批量操作筛选栏 -->
    <div class="operate-bar">
      <div class="left-opt">
        <a-checkbox v-model:checked="isAllCheck" @change="handleCheckAll"
          >全选</a-checkbox
        >
        <span
          class="text-btn"
          :class="{ disabled: downloading }"
          @click="handleDownload"
          >{{ downloading ? "下载中..." : "下载" }}</span
        >
        <span class="text-btn" @click="handleCancelBatch">批量取消收藏</span>
      </div>
      <div class="right-opt">
        <!-- 排序下拉：收藏时间 -->
        <a-select
          v-model:value="sortType"
          placeholder="按收藏时间排序"
          style="width: 200px"
          @change="fetchData"
        >
          <a-select-option value="1">正序</a-select-option>
          <a-select-option value="2">倒序</a-select-option>
        </a-select>

        <!-- 类型筛选下拉（同步tab） -->
        <a-select
          v-model:value="activeTab"
          placeholder="全部类型"
          style="width: 200px; margin-right: 12px"
          @change="filterData"
        >
          <a-select-option
            v-for="tabItem in tabList"
            :key="tabItem.value"
            :value="tabItem.value"
          >
            {{ tabItem.label }}
          </a-select-option>
        </a-select>
      </div>
    </div>

    <!-- 收藏内容列表 -->
    <div class="collect-list">
      <div v-for="row in displayList" :key="row.id" class="collect-card">
        <div class="card-left">
          <a-checkbox
            v-model:checked="row.checked"
            @change="updateAllCheckState"
          />
          <div class="info-wrap">
            <div class="card-title" @click="$emit('view-detail', row)">
              {{ row.caseNumber || row.fileName }}
            </div>
            <div class="card-tag">
              {{ row.causeOfAction || row.fileType }} | {{ row.court }} |
              {{ row.createTime }}
            </div>
            <!-- <div class="card-court">{{ row.court }}</div> -->
            <div class="card-summary">{{ row.caseSummary }}</div>
            <!-- <div class="card-desc">{{ row.createTime }}</div> -->
          </div>
        </div>
        <a-button class="cancel-btn" @click="handleSingleCancel(row)"
          >取消收藏</a-button
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { message } from "ant-design-vue";
import { getFolderFiles, removeCaseFavorite } from "../../api/collect/folder";
import { BatchDownloadCases } from "../../api/case/case";

const emit = defineEmits(["backList", "view-detail"]);
const props = defineProps({
  folderInfo: {
    type: Object,
    default: () => ({ name: "默认收藏夹" }),
  },
});

// Tab - 从 groups 动态生成
const tabList = ref([{ value: "all", label: "全部" }]);
const activeTab = ref("all");

// 排序类型 desc=新→旧  asc=旧→新
const sortType = ref("1");

// 原始数据
const rawItems = ref([]);

// 根据 tab + 排序 筛选后的数据
const displayList = ref([]);

const filterData = () => {
  // 1. 先按类型过滤
  let tempList = [];
  if (activeTab.value === "all") {
    tempList = rawItems.value.map((item) => ({ ...item, checked: false }));
  } else {
    tempList = rawItems.value
      .filter((item) => item.favoriteType === activeTab.value)
      .map((item) => ({ ...item, checked: false }));
  }

  // 2. 按收藏时间排序（createTime字符串日期比较）
  tempList.sort((a, b) => {
    const timeA = new Date(a.createTime).getTime();
    const timeB = new Date(b.createTime).getTime();
    if (sortType.value === "1") {
      return timeB - timeA; // 新的在前
    } else {
      return timeA - timeB; // 旧的在前
    }
  });

  displayList.value = tempList;
  // 切换筛选/排序后清空全选
  isAllCheck.value = false;
};

// 全选状态
const isAllCheck = ref(false);
const handleCheckAll = () => {
  displayList.value.forEach((item) => (item.checked = isAllCheck.value));
};

// 监听子项选中变化，自动更新全选状态
const updateAllCheckState = () => {
  const list = displayList.value;
  if (list.length === 0) {
    isAllCheck.value = false;
    return;
  }
  isAllCheck.value = list.every((item) => item.checked);
};

// 获取数据
const fetchData = async () => {
  try {
    const res = await getFolderFiles(
      props.folderInfo.id,
      Number(sortType.value),
    );
    if (res?.data) {
      const data = res.data;
      rawItems.value = data.items || [];
      filterData();

      // 从 items 提取不重复的 favoriteType 生成 tab
      const typeSet = [
        ...new Set(
          (data.items || []).map((i) => i.favoriteType).filter(Boolean),
        ),
      ];
      const countMap = {};
      (data.items || []).forEach((i) => {
        countMap[i.favoriteType] = (countMap[i.favoriteType] || 0) + 1;
      });
      const groupTabs = typeSet.map((t) => ({
        value: t,
        label: `${t} (${countMap[t] || 0})`,
      }));
      tabList.value = [
        { value: "all", label: `全部 (${rawItems.value.length})` },
        ...groupTabs,
      ];
    }
  } catch (err) {
    console.error("获取收藏内容失败", err);
    message.error("获取收藏内容失败");
  }
};

// 批量取消收藏
const handleCancelBatch = async () => {
  const checkedItems = displayList.value.filter((item) => item.checked);
  if (!checkedItems.length) {
    message.warning("请先选择要取消收藏的案例");
    return;
  }
  const ids = checkedItems.map((item) => item.id).filter(Boolean);
  if (!ids.length) {
    message.warning("所选案例无 ID，无法取消收藏");
    return;
  }
  try {
    await removeCaseFavorite(ids);
    message.success("取消收藏成功");
    fetchData();
  } catch (err) {
    console.error("批量取消收藏失败", err);
    message.error("取消收藏失败");
  }
};

// 单条取消收藏
const handleSingleCancel = async (row) => {
  if (!row?.id) {
    message.warning("该记录无法取消收藏");
    return;
  }
  try {
    await removeCaseFavorite([row.id]);
    message.success("取消收藏成功");
    fetchData();
  } catch (err) {
    console.error("取消收藏失败", err);
    message.error("取消收藏失败");
  }
};

const downloading = ref(false);

// 批量下载（与 CaseListPanel.handleBatchDownload 相同逻辑）
const handleDownload = async () => {
  const checkedItems = displayList.value.filter((item) => item.checked);
  if (!checkedItems.length) {
    message.warning("请先勾选要下载的案例");
    return;
  }

  const caseNumbers = checkedItems
    .map((item) => item.caseNumber || "")
    .filter(Boolean);
  if (!caseNumbers.length) {
    message.warning("所选案例无案号，无法下载");
    return;
  }

  downloading.value = true;
  try {
    const blob = await BatchDownloadCases({ caseNumbers });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `案例批量下载_${Date.now()}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    message.success("下载成功");
  } catch (err) {
    console.error("批量下载失败", err);
    message.error("下载失败");
  } finally {
    downloading.value = false;
  }
};
// 废弃原空函数，不再使用
// const handleBatchCancel = () => {}

watch(
  () => props.folderInfo?.id,
  (val) => {
    if (val) {
      fetchData();
    }
  },
);

onMounted(() => {
  if (props.folderInfo?.id) {
    fetchData();
  }
});
</script>

<style scoped lang="less">
@pageBg: #f5f7fc;
@cardWhite: #ffffff;
@textMain: #1d2129;
@textGray: #86909c;
@textLight: #4e5969;
@primaryBlue: #4080ff;
@primaryLight: #e8f3ff;
@borderLine: #e5e7eb;
@radiusSm: 6px;
@radiusMd: 10px;

.detail-page {
  width: 100%;
  .back-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 24px;
    font-size: 26px;
    font-weight: 600;
    color: @textMain;
    cursor: pointer;
    img {
      width: 36px;
      height: 36px;
    }
  }

  // ========== Tab栏 ==========
  .tab-group {
    margin-bottom: 18px;

    .tab-wrap {
      display: flex;
      gap: 8px;

      .tab-item {
        padding: 5px 14px;
        font-size: 13px;
        line-height: 1.4;
        border-radius: 20px;
        border: 1px solid transparent;
        background: #f0f2f5;
        color: @textGray;
        cursor: pointer;
        transition: 0.22s ease;
        white-space: nowrap;

        &:hover {
          background: #e6e8eb;
        }

        &.active {
          background: @primaryLight;
          color: @primaryBlue;
          border-color: @primaryLight;
          font-weight: 500;
        }
      }
    }
  }

  .operate-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: @cardWhite;
    padding: 14px 20px;
    border-radius: @radiusMd;
    margin-bottom: 16px;

    .left-opt {
      display: flex;
      align-items: center;
      gap: 16px;

      .text-btn {
        color: @primaryBlue;
        cursor: pointer;
        font-size: 14px;
      }
    }
    .right-opt {
      display: flex;
      align-items: center;
      gap: 16px;
    }
  }

  .collect-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .collect-card {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    background: @cardWhite;
    padding: 18px 22px;
    border-radius: @radiusMd;
    border: 1px solid @borderLine;

    .card-left {
      display: flex;
      gap: 12px;
      align-items: flex-start;

      .info-wrap {
        .card-title {
          font-family: PingFangSC, PingFang SC;
          font-weight: 600;
          font-size: 16px;
          color: #165dff;
          margin-bottom: 8px;
          cursor: pointer;
        }

        .card-tag {
          font-family: PingFangSC, PingFang SC;
          font-weight: 400;
          font-size: 14px;
          color: #5f6a7a;
          line-height: 16px;
          margin-bottom: 6px;
        }

        .card-court {
          font-size: 12px;
          color: @textLight;
          margin-bottom: 2px;
        }

        .card-summary {
          font-family: "仿宋_GB2312", SimSun, serif;
          font-size: clamp(13px, 1.4vw, 16pt);
          font-weight: 400;
          line-height: clamp(20px, 2.4vw, 28pt);
          color: #52627b;
          white-space: pre-wrap;
          word-break: break-word;
          margin-bottom: 4px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-desc {
          font-size: 13px;
          color: @textLight;
          line-height: 1.5;
        }
      }
    }

    .cancel-btn {
      background: #fff7e6;
      color: #e6a23c;
      border: 1px solid #ffe699;
      border-radius: @radiusSm;
    }
  }
}

:deep(.ant-btn) {
  border-radius: @radiusSm;
}
</style>
